import type { ReactNode } from 'react'
import type { WorkspaceMembership, WorkspaceWithDetails } from '@/lib/api/workspaces'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, useContext, useEffect, useState } from 'react'
import { workspacesApi } from '@/lib/api/workspaces'
import { useAuth } from './AuthContext'

interface WorkspaceContextType {
  currentWorkspace: WorkspaceWithDetails | null
  currentMembership: WorkspaceMembership | null
  workspaces: WorkspaceWithDetails[]
  isLoading: boolean
  error: Error | null
  setCurrentWorkspace: (workspace: WorkspaceWithDetails) => void
  switchWorkspace: (workspaceId: string) => Promise<void>
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined)

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [currentWorkspace, setCurrentWorkspace] = useState<WorkspaceWithDetails | null>(null)
  const [currentMembership, setCurrentMembership] = useState<WorkspaceMembership | null>(null)

  // Fetch user's workspaces
  const {
    data: workspaces = [],
    isLoading: workspacesLoading,
    error,
  } = useQuery({
    queryKey: ['workspaces'],
    queryFn: () => workspacesApi.getWorkspaces(),
    enabled: !!user,
  })

  // Fetch current user's membership when workspace changes
  const { data: membership, isLoading: membershipLoading } = useQuery({
    queryKey: ['workspace-membership', currentWorkspace?.id],
    queryFn: () => workspacesApi.getWorkspaceMembership(currentWorkspace!.id),
    enabled: !!currentWorkspace?.id,
  })

  const isLoading = workspacesLoading || membershipLoading

  // Set default workspace when workspaces are loaded
  useEffect(() => {
    if (workspaces.length > 0 && !currentWorkspace) {
      // Try to restore from localStorage or use the first workspace
      const savedWorkspaceId = localStorage.getItem('currentWorkspaceId')
      const savedWorkspace = workspaces.find(w => w.id === savedWorkspaceId)

      if (savedWorkspace) {
        setCurrentWorkspace(savedWorkspace)
      }
      else {
        setCurrentWorkspace(workspaces[0])
      }
    }
  }, [workspaces, currentWorkspace])

  // Update membership when it's fetched
  useEffect(() => {
    if (membership) {
      setCurrentMembership(membership)
    }
  }, [membership])

  // Save current workspace to localStorage
  useEffect(() => {
    if (currentWorkspace) {
      localStorage.setItem('currentWorkspaceId', currentWorkspace.id)
      // Set the workspace header for API calls
      const headers = new Headers()
      headers.set('X-Workspace-Id', currentWorkspace.id)
    }
  }, [currentWorkspace])

  const switchWorkspace = async (workspaceId: string): Promise<void> => {
    // Early return if switching to same workspace
    if (workspaceId === currentWorkspace?.id)
      return

    const workspace = workspaces.find(w => w.id === workspaceId)
    if (workspace) {
      // Set localStorage first to ensure header is updated before any cache operations
      localStorage.setItem('currentWorkspaceId', workspace.id)

      // Cancel any in-flight queries to avoid old-workspace responses
      await queryClient.cancelQueries()

      // Remove all queries from cache to prevent cross-workspace data contamination
      queryClient.removeQueries()

      // Set the new workspace
      setCurrentWorkspace(workspace)
    }
  }

  return (
    <WorkspaceContext.Provider
      value={{
        currentWorkspace,
        currentMembership,
        workspaces,
        isLoading,
        error: error as Error | null,
        setCurrentWorkspace,
        switchWorkspace,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  )
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext)
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider')
  }
  return context
}
